import { useEffect, useState } from 'react';
import { useProducts } from '../store/productStore';
import { AdminProduct } from '../lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'rejected', label: 'Rejected' },
];

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

function Products() {
  const {
    products,
    total,
    isLoading,
    error,
    statusFilter,
    searchQuery,
    setStatusFilter,
    setSearchQuery,
    fetchProducts,
    approveProduct,
    rejectProduct,
    deleteProduct,
  } = useProducts();

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [pendingId, setPendingId] = useState<number | null>(null);

  useEffect(() => {
    fetchProducts().catch((e: Error) => toast.error(e.message));
  }, [fetchProducts, statusFilter, searchQuery]);

  const handleApprove = async (product: AdminProduct) => {
    setPendingId(product.id);
    try {
      await approveProduct(product.id);
      toast.success(`"${product.name}" approved`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPendingId(null);
    }
  };

  const openReject = (product: AdminProduct) => {
    setRejectingId(product.id);
    setRejectReason('');
  };

  const handleReject = async (product: AdminProduct) => {
    setPendingId(product.id);
    try {
      await rejectProduct(product.id, rejectReason.trim() || undefined);
      toast.success(`"${product.name}" rejected`);
      setRejectingId(null);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPendingId(null);
    }
  };

  const handleDelete = async (product: AdminProduct) => {
    if (!window.confirm(`Delete "${product.name}" permanently?`)) return;
    setPendingId(product.id);
    try {
      await deleteProduct(product.id);
      toast.success('Product deleted');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="container">
      <h1 className="page-title">Product Management</h1>
      <p className="page-subtitle">
        {total} product{total === 1 ? '' : 's'}.
      </p>

      <div className="toolbar">
        <input
          type="text"
          placeholder="Search products…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSearchQuery(searchInput.trim());
          }}
        />
        <select
          value={statusFilter || ''}
          onChange={(e) => setStatusFilter(e.target.value || null)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => setSearchQuery(searchInput.trim())}
        >
          Search
        </button>
      </div>

      {error && <p className="form-error">{error}</p>}

      {isLoading && products.length === 0 ? (
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      ) : products.length === 0 ? (
        <div className="panel">
          <p className="empty-state">No products found.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Vendor</th>
                <th>Category</th>
                <th>Price</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>
                    {product.name}
                    {product.rejection_reason && (
                      <div className="rejection-note">Rejected: {product.rejection_reason}</div>
                    )}
                  </td>
                  <td>{product.vendor_name || `Vendor #${product.vendor_id}`}</td>
                  <td>{product.category}</td>
                  <td>€{product.price.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge status--${product.status}`}>
                      {product.status}
                    </span>
                  </td>
                  <td>{formatDate(product.created_at)}</td>
                  <td>
                    <div className="actions">
                      {product.status === 'pending' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--primary"
                          disabled={pendingId === product.id}
                          onClick={() => handleApprove(product)}
                        >
                          Approve
                        </button>
                      )}
                      {product.status === 'pending' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--danger"
                          onClick={() => openReject(product)}
                        >
                          Reject
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--sm btn--danger"
                        disabled={pendingId === product.id}
                        onClick={() => handleDelete(product)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejectingId !== null && (
        <div className="modal-overlay" onClick={() => setRejectingId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Product</h3>
              <button type="button" className="modal-close" onClick={() => setRejectingId(null)}>
                &times;
              </button>
            </div>
            <div className="form-row">
              <label htmlFor="reason">Rejection reason (optional)</label>
              <textarea
                id="reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Tell the vendor why the asset was rejected…"
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setRejectingId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={pendingId === rejectingId}
                onClick={() => {
                  const product = products.find((p) => p.id === rejectingId);
                  if (product) handleReject(product);
                }}
              >
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
